import { Typography } from "@/components/atoms";
// PhoneIcon, UserIcon: sales agent contact hidden for now
import { CalendarIcon, ClockIcon } from "@/components/atoms/icons";
import { DashedDivider } from "@/components/molecules";
import type { UIColorTokens } from "@/core/constants/uiColors";
import { useTranslation } from "@/core/hooks";
// import { openPhoneDialer } from "@/core/utils/phoneUtils"; // Sales agent contact hidden for now
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

export interface AppointmentStatusCardProps {
  colors: UIColorTokens;
  t: ReturnType<typeof useTranslation>["t"];
  date: string;
  time: string;
  // Sales agent contact hidden for now
  // agentName: string | undefined;
  // agentPhone: string | undefined;
  onCancelAppointment: () => void;
  onReschedule: () => void;
}

/**
 * BottomSheet content shown when the user already has a confirmed appointment —
 * date/time + agent contact, plus Cancel/Reschedule actions. Shared between
 * PropertyDetailsScreenAdhaMobile (unit-level) and ProjectDetailsScreenAdhaMobile
 * (project-level) so both screens' "View Appointment" flows stay visually and
 * behaviorally identical.
 */
export function AppointmentStatusCard({
  colors,
  t,
  date,
  time,
  // Sales agent contact hidden for now
  // agentName,
  // agentPhone,
  onCancelAppointment,
  onReschedule,
}: Readonly<AppointmentStatusCardProps>) {
  return (
    <View
      style={[
        styles.appointmentStatusCard,
        { backgroundColor: colors.backgroundLevel2 },
      ]}
    >
      <View style={styles.appointmentStatusTopRow}>
        <View style={styles.appointmentStatusContent}>
          <Typography
            variant="headline"
            color="primary"
            style={styles.appointmentStatusTitle}
          >
            {t("propertyDetails.appointmentConfirmedTitle")}
          </Typography>
          <DashedDivider color={colors.outlineDisabled} />
          <View style={[styles.appointmentMetaRow, styles.appointmentDateRow]}>
            <View style={styles.appointmentMetaColumn}>
              <Typography variant="footnote" color="secondary">
                {t("propertyDetails.date")}
              </Typography>
              <View style={styles.appointmentMetaItem}>
                <CalendarIcon size={14} color={colors.fontSecondary} />
                <Typography
                  variant="caption"
                  color="secondary"
                  style={styles.appointmentMetaText}
                >
                  {date}
                </Typography>
              </View>
            </View>
            <View style={styles.appointmentMetaColumnFill}>
              <Typography variant="footnote" color="secondary">
                {t("propertyDetails.time")}
              </Typography>
              <View style={styles.appointmentMetaItem}>
                <ClockIcon size={14} color={colors.fontSecondary} />
                <Typography
                  variant="caption"
                  color="secondary"
                  style={styles.appointmentMetaText}
                >
                  {time}
                </Typography>
              </View>
            </View>
          </View>
          {/* Sales agent contact (hidden for now)
          {(agentName || agentPhone) && (
            <View style={[styles.appointmentMetaRow, styles.appointmentContactRow]}>
              {agentName ? (
                <View style={styles.appointmentMetaColumn}>
                  <Typography variant="footnote" color="secondary">
                    {t("propertyDetails.contactPersonName")}
                  </Typography>
                  <View style={styles.appointmentMetaItem}>
                    <UserIcon size={14} color={colors.fontSecondary} />
                    <Typography
                      variant="caption"
                      color="secondary"
                      style={styles.appointmentMetaText}
                    >
                      {agentName}
                    </Typography>
                  </View>
                </View>
              ) : null}
              {agentPhone ? (
                <View style={styles.appointmentMetaColumnFill}>
                  <Typography variant="footnote" color="secondary">
                    {t("propertyDetails.phoneNumber")}
                  </Typography>
                  <TouchableOpacity
                    style={styles.appointmentMetaItem}
                    onPress={() => openPhoneDialer(agentPhone)}
                  >
                    <PhoneIcon size={13} color={colors.fontSecondary} />
                    <Typography
                      variant="caption"
                      color="secondary"
                      style={[styles.appointmentMetaText, styles.phoneNumberText]}
                    >
                      {agentPhone}
                    </Typography>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          )}
          */}
        </View>
      </View>
      <View style={styles.appointmentActionRow}>
        <TouchableOpacity
          style={styles.appointmentTextButton}
          onPress={onCancelAppointment}
        >
          <Typography variant="headline" style={styles.appointmentActionText}>
            {t("propertyDetails.cancelAppointment")}
          </Typography>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.appointmentRescheduleButton}
          onPress={onReschedule}
        >
          <Typography variant="headline" style={styles.appointmentRescheduleText}>
            {t("propertyDetails.reschedule")}
          </Typography>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  appointmentStatusCard: {
    borderRadius: 16,
    padding: 16,
    gap: 14,
  },
  appointmentStatusTopRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-start",
  },
  appointmentStatusContent: {
    flex: 1,
    gap: 8,
  },
  appointmentStatusTitle: {
    color: "#333333",
    fontFamily: "Matter-SemiBold",
    fontWeight: "600",
    fontSize: 18,
    lineHeight: 18,
    letterSpacing: 0,
  },
  appointmentMetaRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  appointmentDateRow: {
    marginTop: 12,
  },
  appointmentContactRow: {
    marginTop: 12,
  },
  appointmentMetaColumn: {
    width: 150,
    gap: 4,
  },
  appointmentMetaColumnFill: {
    flex: 1,
    gap: 4,
    paddingLeft: 20,
  },
  appointmentMetaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 1,
  },
  appointmentMetaText: {
    color: "#4D4D4D",
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    fontSize: 12,
    lineHeight: 12,
    letterSpacing: 0,
    flexShrink: 1,
  },
  phoneNumberText: {
    textDecorationLine: "underline",
  },
  appointmentActionRow: {
    flexDirection: "row",
    alignItems: "center",
    height: 48,
    gap: 12,
  },
  appointmentTextButton: {
    flex: 1,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
  },
  appointmentActionText: {
    color: "#1A1A1A",
    fontFamily: "Matter-SemiBold",
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 16,
    letterSpacing: 0,
    textAlign: "center",
    textDecorationLine: "underline",
  },
  appointmentRescheduleButton: {
    flex: 1,
    height: 48,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#1A1A1A",
    justifyContent: "center",
    alignItems: "center",
  },
  appointmentRescheduleText: {
    color: "#1A1A1A",
    fontFamily: "Matter-SemiBold",
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 16,
    letterSpacing: 0,
    textAlign: "center",
  },
});

export default AppointmentStatusCard;
